import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import LinkIcon from '@mui/icons-material/Link';
import InfoIcon from '@mui/icons-material/Info';
import { courseLinkageApi, linkageApi, VersionMetadata, contentApi } from '../services/api';

interface CourseLinkageDialogProps {
  open: boolean;
  onClose: () => void;
  bucketId: string;
  environmentId: string;
  courseFilename: string;
  courseName?: string;
}

interface ActivityLinkageInfo {
  activityFilename: string;
  activityName?: string;
  currentVersion: string;
  availableVersions: VersionMetadata[];
  auLinkages: Record<string, string>;
  isEditing: boolean;
  newVersion: string;
}

const CourseLinkageDialog: React.FC<CourseLinkageDialogProps> = ({
  open,
  onClose,
  bucketId,
  environmentId,
  courseFilename,
  courseName,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [courseLinkages, setCourseLinkages] = useState<Record<string, string>>({});
  const [activityInfos, setActivityInfos] = useState<ActivityLinkageInfo[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [addingActivity, setAddingActivity] = useState(false);
  const [availableActivities, setAvailableActivities] = useState<Array<{ filename: string; name?: string; currentVersion: string }>>([]);
  const [selectedActivity, setSelectedActivity] = useState<string>('');

  useEffect(() => {
    if (open) {
      loadLinkages();
    }
  }, [open, bucketId, environmentId, courseFilename]);

  const loadLinkages = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load course→activity linkages
      const linkages = await courseLinkageApi.getCourseLinkages(bucketId, environmentId, courseFilename);
      setCourseLinkages(linkages);

      // Load activity files to get names
      const activityFiles = await contentApi.getFolderContentsWithVersions('cmi5_activities');

      // For each activity, load version info and AU linkages
      const activityInfosPromises = Object.entries(linkages).map(async ([activityFilename, version]) => {
        try {
          // Get activity name from file metadata (try different key formats)
          const activityFile = activityFiles.find(f => f.name === activityFilename);
          const activityName = activityFile?.metadata?.['Cmi5-activity-name']
            || activityFile?.metadata?.['cmi5-activity-name']
            || activityFile?.metadata?.['activity-name']
            || activityFile?.metadata?.['Activity Name']
            || activityFile?.metadata?.['activityname']
            || activityFile?.metadata?.['name']
            || activityFile?.metadata?.['title']
            || undefined;  // Return undefined if no name found, so we can show filename only

          // Get available versions for this activity
          let availableVersions: VersionMetadata[] = [];
          try {
            availableVersions = await contentApi.getFileVersions('cmi5_activities', activityFilename);
          } catch (versionError) {
            console.warn(`Failed to load versions for ${activityFilename}:`, versionError);
            // Fallback to current version only
            availableVersions = [{
              version,
              timestamp: new Date().toISOString(),
              uploadedBy: 'unknown',
              isCurrent: true,
              s3Key: '',
              size: 0
            }];
          }

          // Get AU→scenario linkages for this activity
          let auLinkages: Record<string, string> = {};
          try {
            auLinkages = await linkageApi.getLinkages(bucketId, environmentId, activityFilename);
          } catch (auError) {
            console.warn(`Failed to load AU linkages for ${activityFilename}:`, auError);
          }

          return {
            activityFilename,
            activityName,
            currentVersion: version,
            availableVersions,
            auLinkages,
            isEditing: false,
            newVersion: version,
          };
        } catch (activityError) {
          console.error(`Failed to load info for ${activityFilename}:`, activityError);
          return {
            activityFilename,
            currentVersion: version,
            availableVersions: [],
            auLinkages: {},
            isEditing: false,
            newVersion: version,
          };
        }
      });

      const activityInfos = await Promise.all(activityInfosPromises);
      setActivityInfos(activityInfos);

      // Load available activities (those not already linked)
      const linkedActivityFilenames = new Set(Object.keys(linkages));
      const unlinkedActivities = activityFiles
        .filter(f => !linkedActivityFilenames.has(f.name))
        .map(f => ({
          filename: f.name,
          name: f.metadata?.['Cmi5-activity-name']
            || f.metadata?.['cmi5-activity-name']
            || f.metadata?.['activity-name']
            || f.metadata?.['Activity Name']
            || f.metadata?.['activityname']
            || f.metadata?.['name']
            || f.metadata?.['title']
            || undefined,
          currentVersion: f.versionInfo?.currentVersion || 'v1',
        }));
      setAvailableActivities(unlinkedActivities);
    } catch (err) {
      console.error('Failed to load linkages:', err);
      setError(err instanceof Error ? err.message : 'Failed to load linkages');
    } finally {
      setLoading(false);
    }
  };

  const handleEditActivity = (index: number) => {
    setActivityInfos(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isEditing: true };
      return updated;
    });
  };

  const handleCancelEdit = (index: number) => {
    setActivityInfos(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        isEditing: false,
        newVersion: updated[index].currentVersion,
      };
      return updated;
    });
  };

  const handleVersionChange = (index: number, newVersion: string) => {
    setActivityInfos(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], newVersion };
      return updated;
    });
    setHasChanges(true);
  };

  const handleSaveActivity = async (index: number) => {
    const activityInfo = activityInfos[index];

    if (activityInfo.newVersion === activityInfo.currentVersion) {
      handleCancelEdit(index);
      return;
    }

    try {
      await courseLinkageApi.setActivityLinkage(
        bucketId,
        environmentId,
        courseFilename,
        activityInfo.activityFilename,
        activityInfo.newVersion
      );

      setActivityInfos(prev => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          currentVersion: updated[index].newVersion,
          isEditing: false,
        };
        return updated;
      });

      setCourseLinkages(prev => ({
        ...prev,
        [activityInfo.activityFilename]: activityInfo.newVersion,
      }));

      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update linkage');
    }
  };

  const handleAddActivity = async () => {
    if (!selectedActivity) return;

    const activity = availableActivities.find(a => a.filename === selectedActivity);
    if (!activity) return;

    try {
      setError(null);
      await courseLinkageApi.setActivityLinkage(
        bucketId,
        environmentId,
        courseFilename,
        activity.filename,
        activity.currentVersion
      );

      // Close add dialog
      setAddingActivity(false);
      setSelectedActivity('');

      // Reload linkages to reflect the new activity
      await loadLinkages();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add activity linkage');
    }
  };

  const handleRemoveActivity = async (index: number) => {
    const activityInfo = activityInfos[index];

    if (!confirm(`Remove linkage for ${activityInfo.activityFilename}?`)) {
      return;
    }

    try {
      await courseLinkageApi.removeActivityLinkage(
        bucketId,
        environmentId,
        courseFilename,
        activityInfo.activityFilename
      );

      // Reload linkages to update both activity list and available activities
      await loadLinkages();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove linkage');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh', maxHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <LinkIcon />
          <Typography variant="h6" component="span">
            Course Linkages
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {courseName || courseFilename}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!loading && activityInfos.length === 0 && (
          <Alert severity="info">
            No activity linkages found for this course. Linkages will be created automatically during export.
          </Alert>
        )}

        {!loading && (
          <Box>
            {activityInfos.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }} icon={<InfoIcon />}>
                This course has {activityInfos.length} linked {activityInfos.length === 1 ? 'activity' : 'activities'}.
                You can change which version of each activity will be imported.
              </Alert>
            )}

            {/* Add Activity Section */}
            {!addingActivity ? (
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setAddingActivity(true)}
                  disabled={availableActivities.length === 0}
                >
                  + Add Activity
                </Button>
                {availableActivities.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                    All activities are already linked
                  </Typography>
                )}
              </Box>
            ) : (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Add Activity to Course
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <FormControl size="small" sx={{ minWidth: 300 }}>
                    <InputLabel>Select Activity</InputLabel>
                    <Select
                      value={selectedActivity}
                      label="Select Activity"
                      onChange={(e) => setSelectedActivity(e.target.value)}
                    >
                      {availableActivities.map((activity) => (
                        <MenuItem key={activity.filename} value={activity.filename}>
                          {activity.name || activity.filename} ({activity.currentVersion})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleAddActivity}
                    disabled={!selectedActivity}
                  >
                    Add
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setAddingActivity(false);
                      setSelectedActivity('');
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Paper>
            )}

            {activityInfos.map((activityInfo, index) => (
              <Accordion key={activityInfo.activityFilename} defaultExpanded={index === 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={2} width="100%">
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {activityInfo.activityName || activityInfo.activityFilename}
                      </Typography>
                      {activityInfo.activityName && (
                        <Typography variant="caption" color="text.secondary">
                          {activityInfo.activityFilename}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={activityInfo.currentVersion}
                      color="primary"
                      size="small"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    {/* Version Selection */}
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Activity Version
                      </Typography>

                      {!activityInfo.isEditing ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2">
                            Using version: <strong>{activityInfo.currentVersion}</strong>
                          </Typography>
                          <Tooltip title="Change version">
                            <IconButton
                              size="small"
                              onClick={() => handleEditActivity(index)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove linkage">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveActivity(index)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Box display="flex" alignItems="center" gap={1}>
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Version</InputLabel>
                            <Select
                              value={activityInfo.newVersion}
                              label="Version"
                              onChange={(e) => handleVersionChange(index, e.target.value)}
                            >
                              {activityInfo.availableVersions.map((v) => (
                                <MenuItem key={v.version} value={v.version}>
                                  {v.version} {v.isCurrent && '(current)'}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Tooltip title="Save">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleSaveActivity(index)}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <IconButton
                              size="small"
                              onClick={() => handleCancelEdit(index)}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Paper>

                    {/* AU→Scenario Linkages */}
                    {Object.keys(activityInfo.auLinkages).length > 0 && (
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                        <Typography variant="subtitle2" gutterBottom>
                          AU→Scenario Linkages ({Object.keys(activityInfo.auLinkages).length})
                        </Typography>
                        <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
                          {Object.entries(activityInfo.auLinkages).map(([auId, scenarioRef]) => {
                            const [scenarioUuid, version] = scenarioRef.split(':');
                            const shortUuid = scenarioUuid.substring(0, 8) + '...';
                            return (
                              <Box key={auId} display="flex" alignItems="center" gap={1} sx={{ py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                  AU: <em>{auId.split('/').pop() || auId}</em>
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                                  →
                                </Typography>
                                <Tooltip title={scenarioUuid}>
                                  <Chip
                                    label={`${shortUuid} (${version})`}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              </Box>
                            );
                          })}
                        </Box>
                      </Paper>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CourseLinkageDialog;
